// What a set row asks for.
//
// The server resolves how a prescription is tracked and serves the answer on the
// entry; this decides which inputs that implies. There is deliberately no second
// implementation of the precedence order here — a client that re-derived it
// would eventually disagree with the server, and the symptom would be a page
// asking for kilograms on a plank.

export const TRACKING = {
  WEIGHT: 'weight',
  REPS: 'reps',
  TIME: 'time',
  DISTANCE: 'distance',
};

/**
 * How a prescription is tracked, defaulting to weight.
 *
 * Weight is what every prescription written before tracking types existed is,
 * and what the server falls back to — so a client reading an older response
 * without the field behaves exactly as it did.
 */
export function trackingOf(exercise) {
  const tracking = exercise?.tracking;
  return Object.values(TRACKING).includes(tracking) ? tracking : TRACKING.WEIGHT;
}

/**
 * The fields a set row should offer, for a given tracking type.
 *
 * @returns {{load: boolean, reps: boolean, duration: boolean, distance: boolean}}
 */
export function fieldsFor(tracking) {
  switch (tracking) {
    case TRACKING.TIME:
      return { load: false, reps: false, duration: true, distance: false };
    case TRACKING.DISTANCE:
      return { load: false, reps: false, duration: false, distance: true };
    case TRACKING.REPS:
      return { load: false, reps: true, duration: false, distance: false };
    default:
      return { load: true, reps: true, duration: false, distance: false };
  }
}

/**
 * Decide what a set row means, for tracking types other than weight.
 *
 * The weight-tracked path stays in setEntry.classifySetRow, untouched — it
 * carries the bodyweight regression fix (a blank load means bodyweight, not
 * "skip") and rewriting it here would be a second chance to get that wrong.
 *
 * @returns {{status: 'empty'|'ready'|'incomplete', reason?: string, value?: number}}
 */
export function classifyMeasure(raw, tracking) {
  const text = String(raw ?? '').trim();
  if (text === '') return { status: 'empty' };

  const value = Number(text);
  if (!Number.isFinite(value)) {
    return { status: 'incomplete', reason: `"${text}" is not a number` };
  }
  if (value < 0) {
    return { status: 'incomplete', reason: 'cannot be negative' };
  }

  if (tracking === TRACKING.TIME) {
    if (!Number.isInteger(value)) {
      return { status: 'incomplete', reason: 'seconds must be a whole number' };
    }
    if (value > 3600) {
      return { status: 'incomplete', reason: 'must be 3600 seconds or fewer' };
    }
  }
  if (tracking === TRACKING.DISTANCE && value > 5000) {
    return { status: 'incomplete', reason: 'must be 5000 metres or fewer' };
  }

  return { status: 'ready', value };
}

/**
 * The request body for one logged set, given how it is tracked.
 *
 * Only the fields the tracking type measures are sent. A timed set carries no
 * weight and no reps — sending zeros would record a failed lift alongside a
 * successful hold, and PERF-3 makes a zero meaningful rather than ignorable.
 *
 * `workSeconds`/`restSeconds` are orthogonal to tracking type — they describe
 * how the set was performed, not what it measures, so they are sent whenever
 * the caller has them regardless of weight/reps/time/distance. Omitted, not
 * sent as null, when absent: a caller with no clock running (an older client,
 * a set logged out of sequence) behaves exactly as one that predates this
 * field, the same convention `clientId` already uses just above.
 */
export function buildSetRequest({ tracking, weight, reps, duration, distance, unit, setNumber, weekNumber, sessionDate, clientId, workSeconds, restSeconds }) {
  const body = { setNumber, weekNumber, sessionDate, notes: '' };
  // Only when given: a caller with no key logs exactly as before, and "" would
  // be refused by the server rather than read as "no key" (PERF-17).
  if (clientId) body.clientId = clientId;
  const fields = fieldsFor(tracking);

  if (fields.load) {
    body.weight = weight;
    body.unit = unit;
  }
  if (fields.reps) body.repsPerformed = reps;
  if (fields.duration) body.durationSeconds = duration;
  if (fields.distance) body.distanceMetres = distance;

  if (isSendableTiming(workSeconds)) body.workSeconds = workSeconds;
  if (isSendableTiming(restSeconds)) body.restSeconds = restSeconds;

  return body;
}

/**
 * The server's bound on workSeconds and restSeconds: models.MaxTimingSeconds.
 */
export const MAX_TIMING_SECONDS = 24 * 60 * 60;

/**
 * Whether a timing reading may go to the server at all.
 *
 * Timing is optional, and the server refuses the WHOLE set when a reading is
 * outside 0..MAX_TIMING_SECONDS. A clock that ran across a suspended phone or a
 * day away measured something real but not a set, and sending it meant a 400
 * on every retry: the set could never be saved, and the offline queue held it
 * as failed. A reading that cannot be stored is dropped, never the set.
 */
function isSendableTiming(value) {
  return Number.isInteger(value) && value >= 0 && value <= MAX_TIMING_SECONDS;
}

/** The unit a measure is entered in, for a label. */
export function measureUnit(tracking) {
  if (tracking === TRACKING.TIME) return 'sec';
  if (tracking === TRACKING.DISTANCE) return 'm';
  return '';
}

/**
 * How a logged set reads back.
 *
 * A timed set shows its seconds, not "0 kg" — which is what it would show if the
 * renderer only knew about load, and which reads as a failed lift.
 */
export function describeLoggedSet(performance, unit = 'kg') {
  if (!performance) return '';

  if (performance.durationSeconds !== null && performance.durationSeconds !== undefined) {
    return `${performance.durationSeconds} sec`;
  }
  if (performance.distanceMetres !== null && performance.distanceMetres !== undefined) {
    return `${performance.distanceMetres} m`;
  }

  const reps = performance.repsPerformed;
  // A zero load is bodyweight, not a missing value — the distinction the whole
  // logger turns on.
  const load = performance.weight === 0 ? 'Bodyweight' : `${performance.weight} ${unit}`;
  return reps === undefined || reps === null ? load : `${load} × ${reps}`;
}
